// Source of truth for the topic picker and the /learn/:slug pages.
//
// Hand-picked and hand-written on purpose: these URLs have to stay stable for SEO
// and every subject has to survive human review before a 14-year-old arriving from
// TikTok can quiz on it. Do not generate this list.
//
// Slugs must match backend/topic_seeds.json, which maps each one to its Wikipedia
// article — backend/tests/unit/test_topics.py enforces that. `scripts/generate-seo.mjs`
// reads the `slug:` fields here to build the sitemap and prerender list.

export const topicCategories = [
  {
    id: "nursing",
    name: "Nursing & NCLEX",
    icon: "Stethoscope",
    blurb: "The clinical ground the NCLEX keeps coming back to.",
    topics: [
      {
        slug: "pharmacology",
        wikipedia: "Pharmacology",
        name: "Pharmacology",
        blurb:
          "How drugs move through the body, what they do once they arrive, and why the same dose lands differently in different patients.",
        faqs: [
          {
            question: "Why do I keep missing pharmacology questions?",
            answer:
              "Most people study drugs one at a time, so every new name feels like starting from scratch. Questions are usually written at the class level — if you know what a whole class does, what it is monitored for, and what it interacts with, you can answer about a drug you have never seen before. Work class-first and learn the exceptions after.",
          },
          {
            question: "Do I have to memorise every drug?",
            answer:
              "No, and trying to is the most common way people run out of study time. Learn the classes, the suffixes that signal them (-olol, -pril, -statin, -azole), the assessment you do before administering, and the sign that the drug is causing harm. Individual brand names matter far less than the pattern.",
          },
          {
            question: "What is the fastest way to review pharmacology?",
            answer:
              "Active recall against questions, not re-reading a drug list. Re-reading feels productive and predicts very little; answering, getting it wrong, and finding out why is what actually moves a score. Quiz one class, review only what you missed, then repeat.",
          },
        ],
      },
      {
        slug: "lab-values",
        wikipedia: "Reference ranges for blood tests",
        name: "Lab Values",
        blurb:
          "The normal ranges, what pushes a result outside them, and which values call for action before anything else happens.",
        faqs: [
          {
            question: "Which lab values do I actually need to know?",
            answer:
              "The recurring ones: electrolytes, glucose, kidney and liver markers, the complete blood count, and coagulation studies. Knowing a number is only half of it — the questions usually turn on what you do about an abnormal result, so pair every range with the response it should trigger.",
          },
          {
            question: "How do I stop mixing up similar ranges?",
            answer:
              "Stop learning them as a flat list. Group them by the system they report on and by what a high or low value means clinically, so the number is attached to a story rather than floating on its own. Ranges you can reconstruct from meaning survive exam pressure; memorised digits often do not.",
          },
          {
            question: "Do the units matter?",
            answer:
              "Yes — a value is meaningless without them, and questions do use unit changes to separate careful readers from quick ones. Learn each range with its units attached from the very first time you see it rather than adding them later.",
          },
        ],
      },
      {
        slug: "fluid-and-electrolytes",
        wikipedia: "Electrolyte",
        name: "Fluid & Electrolytes",
        blurb:
          "Sodium, potassium, calcium and magnesium — what moves them, what that does to a patient, and how the body compensates.",
        faqs: [
          {
            question: "Why is this topic so heavily tested?",
            answer:
              "Because almost every other subject touches it. Medications shift electrolytes, kidney and heart problems shift them, and so do fluids and diet. A question that looks like it is about something else often turns on an electrolyte underneath.",
          },
          {
            question: "How do I keep hyper- and hypo- states straight?",
            answer:
              "Reason from what the electrolyte does rather than memorising two lists of symptoms. Once you know the role potassium plays in muscle and cardiac conduction, the signs of too much and too little follow from it, and you can rebuild them under pressure instead of recalling them.",
          },
          {
            question: "What trips people up most here?",
            answer:
              "Compensation. The body corrects imbalances, so the value you are shown may already be a response to the real problem rather than the problem itself. Ask what the body is trying to fix before deciding what to do.",
          },
        ],
      },
      {
        slug: "infection-control",
        wikipedia: "Infection control",
        name: "Infection Control",
        blurb:
          "Transmission routes, precaution types, and the reasoning behind isolating one patient and not another.",
        faqs: [
          {
            question: "How do I remember which precautions go with which condition?",
            answer:
              "Work from how the organism travels rather than from the disease name. Once you can say whether something spreads by contact, droplet, or air, the precautions follow — and you can handle a condition that was never on your list.",
          },
          {
            question: "Why do I miss questions I thought were easy?",
            answer:
              "Usually because the question is about sequence, not knowledge. Knowing which precaution applies is not the same as knowing the order to put equipment on and take it off, and the order is where the marks are lost.",
          },
          {
            question: "Is hand hygiene really tested that often?",
            answer:
              "It comes up constantly, generally as a distractor that is correct. When an option describes the simplest, most fundamental protective action, it is worth taking seriously rather than dismissing as too obvious.",
          },
        ],
      },
      {
        slug: "vital-signs",
        wikipedia: "Vital signs",
        name: "Vital Signs",
        blurb:
          "What each measurement is really reporting, what counts as a change worth acting on, and what to do first when one shifts.",
        faqs: [
          {
            question: "Is it enough to memorise the normal ranges?",
            answer:
              "No. Questions rarely stop at whether a value is normal — they ask what it means for this patient and what you do next. A reading slightly outside the range in a stable patient and the same reading in a deteriorating one call for different responses.",
          },
          {
            question: "Which vital sign changes first?",
            answer:
              "That depends on the situation, which is exactly why questions are built around it. Learn the usual order of change for the common deteriorations rather than treating the four measurements as independent numbers.",
          },
          {
            question: "How do I handle trend questions?",
            answer:
              "Read the whole series before looking at the options. A single reading in isolation is often ambiguous while the direction of travel across several is not, and the answer usually depends on the direction.",
          },
        ],
      },
      {
        slug: "the-nursing-process",
        wikipedia: "Nursing process",
        name: "The Nursing Process",
        blurb:
          "Assessment, diagnosis, planning, implementation and evaluation — the framework that decides which answer comes first.",
        faqs: [
          {
            question: "Why does this matter for answering questions?",
            answer:
              "Because it settles most 'what does the nurse do first' items. When several options are all reasonable actions, the one earliest in the process is usually correct — you assess before you intervene unless the situation is immediately dangerous.",
          },
          {
            question: "When do I skip assessment and act?",
            answer:
              "When waiting would cause harm. Airway, breathing and circulation problems are acted on rather than assessed further. Outside those, gathering information before intervening is the safer read of a question.",
          },
          {
            question: "How is this different from prioritisation?",
            answer:
              "The nursing process orders your steps for one patient; prioritisation orders patients against each other. Questions often combine them, so know which one is being asked before choosing.",
          },
        ],
      },
      {
        slug: "medication-routes",
        wikipedia: "Route of administration",
        name: "Medication Routes",
        blurb:
          "Oral, IV, IM, subcutaneous and topical — how the route changes onset, absorption, and what you check before giving it.",
        faqs: [
          {
            question: "Why does the route change the answer?",
            answer:
              "Because it changes how fast the drug acts and how much of it reaches circulation. The same medication given by two routes has different onset times and different warning signs, and questions are frequently built on that difference.",
          },
          {
            question: "What gets checked before administration?",
            answer:
              "The patient's identity, the drug, the dose, the route, the time, and the reason — plus whatever assessment that specific drug demands. Skipping the drug-specific check is what turns a straightforward question into a wrong answer.",
          },
          {
            question: "Which route errors show up most?",
            answer:
              "Confusing routes that sound alike, and giving by a route the formulation was never intended for. If an option involves changing the route from the one prescribed, treat it with suspicion.",
          },
        ],
      },
      {
        slug: "diabetes-mellitus",
        wikipedia: "Diabetes",
        name: "Diabetes Mellitus",
        blurb:
          "Type 1 and type 2, the medications that manage them, and recognising a blood-sugar emergency in either direction.",
        faqs: [
          {
            question: "How do I tell high and low blood sugar apart quickly?",
            answer:
              "By speed and by the pattern of symptoms. One tends to come on rapidly and the other builds over a longer period, and the questions usually give you enough of a time frame to distinguish them if you read for it.",
          },
          {
            question: "How much insulin detail is needed?",
            answer:
              "Enough to reason about timing. Knowing which insulins act quickly and which last a long time, and when each peaks, matters more than memorising every product, because most questions turn on when something happens rather than what it is called.",
          },
          {
            question: "What is the most common mistake here?",
            answer:
              "Treating the number instead of the patient. A blood glucose value on its own does not decide the answer — how the patient looks, what they last ate, and what medication is already on board all change the correct response.",
          },
        ],
      },
      {
        slug: "heart-failure",
        wikipedia: "Heart failure",
        name: "Heart Failure",
        blurb:
          "Left- and right-sided failure, the symptoms each produces, and why the treatments pull in different directions.",
        faqs: [
          {
            question: "How do I keep left and right sided failure straight?",
            answer:
              "Follow where the blood backs up. Failure on one side congests the circuit feeding it, and the symptoms are simply the consequence of that congestion — which means you can derive them rather than memorise two lists.",
          },
          {
            question: "Why are the medications confusing?",
            answer:
              "Because several of them work against each other on purpose, and each has its own monitoring requirement. Learn what each drug is trying to reduce and the assessment it demands, rather than learning them as one undifferentiated group.",
          },
          {
            question: "What should I watch for in the question stem?",
            answer:
              "Weight and fluid status. A change in daily weight is often the earliest signal in these items, and it is easy to skim past while looking for something more dramatic.",
          },
        ],
      },
      {
        slug: "wound-healing",
        wikipedia: "Wound healing",
        name: "Wound Healing",
        blurb:
          "The stages of repair, what stalls them, and how to tell ordinary healing from an early complication.",
        faqs: [
          {
            question: "Do I need the stages in order?",
            answer:
              "Yes, because questions ask whether what you are seeing is expected at this point. The same appearance can be normal at one stage and a warning at another, and only the sequence tells you which.",
          },
          {
            question: "How do I spot infection versus normal healing?",
            answer:
              "By comparing what is described against what the current stage should look like. Some redness and drainage belong to normal repair; the question is whether the amount, the timing and the character fit the stage the patient is in.",
          },
          {
            question: "What slows healing down most?",
            answer:
              "Poor perfusion, poor nutrition, and blood sugar that is not controlled. When a stem mentions any of those in passing, it is usually there to explain why healing is not progressing as expected.",
          },
        ],
      },
    ],
  },
  {
    id: "history",
    name: "History",
    icon: "Landmark",
    blurb: "The turning points that shaped the world you live in.",
    topics: [
      {
        slug: "world-war-2",
        wikipedia: "World War II",
        name: "World War II",
        blurb:
          "The deadliest conflict in human history — how it started, how it spread across six continents, and how it ended.",
      },
      {
        slug: "ancient-rome",
        wikipedia: "Ancient Rome",
        name: "Ancient Rome",
        blurb:
          "From a city on seven hills to an empire that ruled the Mediterranean, and the long unravelling that followed.",
      },
      {
        slug: "the-cold-war",
        wikipedia: "Cold War",
        name: "The Cold War",
        blurb:
          "Forty years of standoff between the United States and the Soviet Union, fought through proxies, spies, and the space race.",
      },
      {
        slug: "ancient-egypt",
        wikipedia: "Ancient Egypt",
        name: "Ancient Egypt",
        blurb: "Three thousand years of pharaohs, pyramids, and hieroglyphs along the Nile.",
      },
      {
        slug: "the-french-revolution",
        wikipedia: "French Revolution",
        name: "The French Revolution",
        blurb:
          "How a bankrupt monarchy became a republic, a terror, and finally an empire — in ten years.",
      },
      {
        slug: "the-renaissance",
        wikipedia: "Renaissance",
        name: "The Renaissance",
        blurb:
          "The rebirth of art, science, and humanism that pulled Europe out of the Middle Ages.",
      },
    ],
  },
  {
    id: "biology",
    name: "Biology",
    icon: "Dna",
    blurb: "How living things are built, and how they keep going.",
    topics: [
      {
        slug: "the-human-cell",
        wikipedia: "Cell (biology)",
        name: "The Human Cell",
        blurb:
          "The smallest unit of life — membranes, organelles, and the machinery that keeps every one of your cells running.",
      },
      {
        slug: "genetics-and-dna",
        wikipedia: "Genetics",
        name: "Genetics & DNA",
        blurb:
          "Genes, chromosomes, and inheritance: how traits get passed down and what happens when the copy goes wrong.",
      },
      {
        slug: "human-anatomy",
        wikipedia: "Human body",
        name: "Human Anatomy",
        blurb: "The organ systems of the human body and how they fit together.",
      },
      {
        slug: "evolution",
        wikipedia: "Evolution",
        name: "Evolution",
        blurb:
          "Natural selection, adaptation, and common descent — the idea that ties all of biology together.",
      },
      {
        slug: "photosynthesis",
        wikipedia: "Photosynthesis",
        name: "Photosynthesis",
        blurb: "How plants turn light into sugar, and why almost every food chain starts there.",
      },
      {
        slug: "the-immune-system",
        wikipedia: "Immune system",
        name: "The Immune System",
        blurb: "Innate and adaptive defence: how your body recognises a threat and remembers it.",
      },
    ],
  },
  {
    id: "space",
    name: "Space",
    icon: "Rocket",
    blurb: "Everything above the atmosphere, from our neighbourhood outward.",
    topics: [
      {
        slug: "the-solar-system",
        wikipedia: "Solar System",
        name: "The Solar System",
        blurb: "The Sun, eight planets, and everything else caught in its gravity.",
      },
      {
        slug: "black-holes",
        wikipedia: "Black hole",
        name: "Black Holes",
        blurb: "Event horizons, singularities, and what happens when gravity wins completely.",
      },
      {
        slug: "stellar-evolution",
        wikipedia: "Stellar evolution",
        name: "Stars & Stellar Evolution",
        blurb: "How stars are born, how they burn, and the very different ways they die.",
      },
      {
        slug: "the-big-bang",
        wikipedia: "Big Bang",
        name: "The Big Bang",
        blurb: "The first moments of the universe and the evidence that tells us they happened.",
      },
      {
        slug: "space-exploration",
        wikipedia: "Space exploration",
        name: "Space Exploration",
        blurb: "From Sputnik to the Moon to robotic probes past the edge of the solar system.",
      },
      {
        slug: "galaxies",
        wikipedia: "Galaxy",
        name: "Galaxies",
        blurb: "Spirals, ellipticals, and the billions of stars bound together in each one.",
      },
    ],
  },
  {
    id: "geography",
    name: "Geography",
    icon: "Globe2",
    blurb: "The physical world and the way it is arranged.",
    topics: [
      {
        slug: "world-capitals",
        wikipedia: "Capital city",
        name: "World Capitals",
        blurb: "What makes a city a capital, and the ones that run the world's countries.",
      },
      {
        slug: "plate-tectonics",
        wikipedia: "Plate tectonics",
        name: "Plate Tectonics",
        blurb: "Drifting plates, spreading ridges, and why earthquakes happen where they do.",
      },
      {
        slug: "climate-zones",
        wikipedia: "Köppen climate classification",
        name: "Climate Zones",
        blurb: "The Köppen system: how climates are classified from tropical to polar.",
      },
      {
        slug: "rivers-and-oceans",
        wikipedia: "Ocean",
        name: "Rivers & Oceans",
        blurb: "Currents, basins, and the water cycle that moves the planet's water around.",
      },
      {
        slug: "volcanoes",
        wikipedia: "Volcano",
        name: "Volcanoes",
        blurb: "Magma, eruption types, and the volcanic belts that ring the Pacific.",
      },
      {
        slug: "world-biomes",
        wikipedia: "Biome",
        name: "World Biomes",
        blurb:
          "Rainforest to tundra — the major ecosystems and what determines where each one sits.",
      },
    ],
  },
  {
    id: "chemistry",
    name: "Chemistry",
    icon: "FlaskConical",
    blurb: "What matter is made of and how it rearranges.",
    topics: [
      {
        slug: "the-periodic-table",
        wikipedia: "Periodic table",
        name: "The Periodic Table",
        blurb: "Groups, periods, and why the elements fall into the pattern they do.",
      },
      {
        slug: "chemical-bonding",
        wikipedia: "Chemical bond",
        name: "Chemical Bonding",
        blurb: "Ionic, covalent, and metallic bonds — how atoms hold on to each other.",
      },
      {
        slug: "acids-and-bases",
        wikipedia: "Acid–base reaction",
        name: "Acids & Bases",
        blurb: "pH, neutralisation, and the proton transfers behind both.",
      },
      {
        slug: "states-of-matter",
        wikipedia: "State of matter",
        name: "The States of Matter",
        blurb: "Solid, liquid, gas, plasma — and what makes a substance switch between them.",
      },
      {
        slug: "organic-chemistry",
        wikipedia: "Organic chemistry",
        name: "Organic Chemistry",
        blurb: "Carbon compounds, functional groups, and the reactions that build them.",
      },
      {
        slug: "chemical-reactions",
        wikipedia: "Chemical reaction",
        name: "Chemical Reactions",
        blurb: "Reactants, products, and the rules that govern how a reaction proceeds.",
      },
    ],
  },
  {
    id: "technology",
    name: "Technology",
    icon: "Cpu",
    blurb: "How the machines you use every day actually work.",
    topics: [
      {
        slug: "how-the-internet-works",
        wikipedia: "Internet",
        name: "How the Internet Works",
        blurb:
          "Packets, protocols, and routing — what happens between typing a URL and seeing a page.",
      },
      {
        slug: "computer-basics",
        wikipedia: "Computer",
        name: "Computer Basics",
        blurb: "CPU, memory, storage, and input — the parts every computer has in common.",
      },
      {
        slug: "artificial-intelligence",
        wikipedia: "Artificial intelligence",
        name: "Artificial Intelligence",
        blurb: "Machine learning, neural networks, and the long history behind the current wave.",
      },
      {
        slug: "cybersecurity",
        wikipedia: "Computer security",
        name: "Cybersecurity",
        blurb: "Threats, defences, and the trade-offs behind keeping a system safe.",
      },
      {
        slug: "programming-concepts",
        wikipedia: "Computer programming",
        name: "Programming Concepts",
        blurb: "Variables, control flow, functions, and data structures — the vocabulary of code.",
      },
      {
        slug: "databases",
        wikipedia: "Database",
        name: "Databases",
        blurb: "Tables, queries, and indexes: how software stores and finds information at scale.",
      },
    ],
  },
];

export const allTopics = topicCategories.flatMap((category) =>
  category.topics.map((topic) => ({
    ...topic,
    categoryId: category.id,
    categoryName: category.name,
  })),
);

export function getTopicBySlug(slug) {
  return allTopics.find((topic) => topic.slug === slug) ?? null;
}

export function getSiblingTopics(slug) {
  const topic = getTopicBySlug(slug);
  if (!topic) return [];
  return allTopics.filter((t) => t.categoryId === topic.categoryId && t.slug !== slug);
}
